import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreVertical, Info, XCircle, RotateCcw, Trash2 } from 'lucide-react';
import type { TimesheetEntry } from '@/types/timesheet';

interface BookingActionMenuProps {
  booking: TimesheetEntry;
  onViewDetails: () => void;
  onDismiss: () => void;
  onRestore: () => void;
  onDelete: () => void;
}

export const BookingActionMenu: React.FC<BookingActionMenuProps> = ({
  booking,
  onViewDetails,
  onDismiss,
  onRestore,
  onDelete,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-accent"
          aria-label="Open booking actions menu"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* View Details - Always available for scheduled bookings */}
        {booking.isFromSchedule && (
          <>
            <DropdownMenuItem onClick={onViewDetails} className="cursor-pointer">
              <Info className="mr-2 h-4 w-4" />
              <span>View Details</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Dismiss - Only for non-dismissed scheduled bookings */}
        {booking.isFromSchedule && !booking.isDismissed && (
          <DropdownMenuItem
            onClick={onDismiss}
            className="cursor-pointer text-orange-600 focus:text-orange-600"
          >
            <XCircle className="mr-2 h-4 w-4" />
            <span>Dismiss Booking</span>
          </DropdownMenuItem>
        )}

        {/* Restore - Only for dismissed bookings */}
        {booking.isFromSchedule && booking.isDismissed && (
          <DropdownMenuItem
            onClick={onRestore}
            className="cursor-pointer text-blue-600 focus:text-blue-600"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            <span>Restore Booking</span>
          </DropdownMenuItem>
        )}

        {/* Delete - Always available */}
        {(booking.isDismissed || !booking.isFromSchedule) && (
          <>
            {booking.isFromSchedule && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={onDelete}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Delete Row</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BookingActionMenu;
